import type { JSX , MouseEventHandler } from "react";
import type { IconComponent } from "../../../atoms/icons/icon-component";
import type { ClassNameProp, IdProp } from "@charterindex/armoury-www";
import type { ButtonBaseProps } from "@mui/material";
import { VisuallyHidden } from "@charterindex/armoury-www";
import { ButtonBase, css, styled } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";


const NavButtonRoot = styled(ButtonBase)(({ theme }) => css`
	height: ${theme.layout.targetSize};
	padding: ${theme.spacing(0, 2)};

	&.slim {
		padding: ${theme.spacing(0, 1)};
	}
	
	justify-content: flex-start;
		
	background-color: var(--navButtonBg, var(--navBackgroundColor));
	
	&:hover {
		--navButtonBg: ${theme.palette.dark.light};
	}

	&.active {
		--navButtonBg: ${theme.palette.brand.main};

		&:hover {
			--navButtonBg: ${theme.palette.brand.light};
		}
	}

	transition: background-color ${theme.transitions.duration.standard}ms;

	font-size: ${theme.typography.body2.fontSize};

	.navButton__icon {
		margin-inline: ${theme.spacing(-.5, 1)};

		&.iconOnly {
			margin-inline: 0;
		}
	}

	&.fullWidth {
		width: 100%;
	}

`) as typeof ButtonBase;

/**
 * Class name applied to `<NavButton>`s
 */
export const NAV_BUTTON_CLASS_NAME = "navButton";
/**
 * Data attribute applied the `<NavButton>`s if clicking it should not close the menu.
 */
export const NAV_BUTTON_NO_CLOSE_MENU_ATTR_NAME = "data-no-close-menu";

export type NavButtonProps = IdProp & ClassNameProp & {
	labelKey: string;
	icon?: IconComponent | undefined;
	iconOnly?: boolean;
	onClick?: MouseEventHandler<HTMLButtonElement>;
	LinkComponent?: React.ElementType;
	href?: string;
	"aria-controls"?: string | undefined;
	"aria-haspopup"?: string | undefined;
	"aria-expanded"?: string | undefined;
	fullWidth?: boolean;
	slim?: boolean;
	active?: boolean;
	noCloseMenu?: boolean;
};

/**
 * Button for the main nav menu.
 */
export const NavButton = ({ className, labelKey, icon: Icon, iconOnly, fullWidth, slim, active, noCloseMenu, ...props }: NavButtonProps): JSX.Element => {
	const { t } = useTranslation();

	return (
		<NavButtonRoot
			className={ classNames(NAV_BUTTON_CLASS_NAME, className, { fullWidth, slim, active }) }
			{ ...props as ButtonBaseProps }
			data-no-close-menu={ noCloseMenu ? "true" : undefined }
		>
			{ Icon && (
				<Icon
					className={ classNames("navButton__icon", { iconOnly }) }
					aria-hidden
				/>
			) }
			{ iconOnly
					? <VisuallyHidden text={ t(labelKey) }/>
					: t(labelKey)
			}
		</NavButtonRoot>
	);
};